import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { CheckIcon } from '@/components/icons'
import { Spinner } from '@/components/Spinner'
import { EligibilityChecklist } from '@/components/programs/EligibilityChecklist'
import { PeakWindowBar } from '@/components/programs/PeakWindowBar'
import { StatusPill } from '@/components/programs/StatusPill'
import * as programsApi from '@/api/programs'
import * as v5 from '@/api/v5'
import { addressLabel } from '@/lib/addresses'
import { trackEvent } from '@/lib/analytics'
import { APP_NAME } from '@/lib/branding'
import {
  ADDRESS_ISSUE_LABEL,
  ADDRESS_ISSUE_MESSAGE,
  addressIssue,
  buildChecklist,
  type Consents,
  deviceInUseMessage,
  formatProgramDate,
  participationBadge,
  participationsByAddress,
  startsRightAway,
  wizardBlocker,
} from '@/lib/programs'
import type { V5AddressSummary } from '@/types/api'
import type { Eligibility, Participation, ProgramInfo } from '@/types/programs'

type Step = 1 | 2 | 3
type Phase = 'intro' | 'wizard' | 'done'

const JOIN_ERRORS: Record<string, string> = {
  ADDRESS_NOT_ELIGIBLE: ADDRESS_ISSUE_MESSAGE.country,
  NOT_ELIGIBLE: 'Niet al je apparaten zijn (nog) verbonden. Controleer ze en probeer het opnieuw.',
  TERMS_VERSION_MISMATCH: 'De voorwaarden zijn net aangepast. Lees ze opnieuw en geef opnieuw akkoord.',
  SIGNUP_CLOSED: 'Aanmelden is inmiddels gesloten.',
  ADDRESS_ALREADY_ENROLLED: 'Dit adres doet al mee via een ander account.',
  REJOIN_NOT_ALLOWED: 'Je bent eerder gestopt met dit programma; opnieuw meedoen kan niet.',
}

const BLOCK_MESSAGES: Partial<Record<NonNullable<Eligibility['blockReason']>, string>> = {
  ADDRESS_ALREADY_ENROLLED: 'Dit adres doet al mee via een ander account. Per adres kan één account meedoen.',
  REJOIN_NOT_ALLOWED: 'Je bent eerder gestopt met dit programma. Opnieuw meedoen kan niet.',
  SIGNUP_CLOSED: 'Aanmelden is gesloten.',
  SIGNUP_NOT_OPEN: 'Aanmelden is nog niet geopend.',
}

const STEP_COPY: Record<Step, { title: string; subtitle: string }> = {
  1: { title: 'Voor welk adres doe je mee?', subtitle: 'Je laadsessies worden per adres gevolgd.' },
  2: {
    title: 'Koppel je apparaten',
    subtitle: `Je auto en Sparky of Flint moeten gekoppeld zijn in de ${APP_NAME}. Een laadpaal is optioneel.`,
  },
  3: { title: 'Bevestig je deelname', subtitle: 'Controleer je gegevens en ga akkoord met de voorwaarden.' },
}

const STEP_CIRCLE = ['bg-dark-purple', 'bg-warning', 'bg-green'] as const

// --- Intro --------------------------------------------------------------------

function Hero({ program }: { program: ProgramInfo }) {
  return (
    <section className="card flex flex-col gap-5 p-6 md:p-10">
      <span className="inline-flex self-start rounded-full bg-light-purple-3 px-4 py-2 text-13 font-bold text-dark-purple">
        Pilot · {formatProgramDate(program.startsAt)} – {formatProgramDate(program.endsAt)}
      </span>
      <h1 className="text-42 font-bold leading-104 tracking-tight md:text-64">{program.name}</h1>
      <p className="max-w-[640px] text-base leading-normal text-dark-blue/70 md:text-lg">
        Laad je auto niet thuis tussen {program.peakWindow.start} en {program.peakWindow.end}, als het
        stroomnet het drukst is. Daarvoor ontvang je na afloop €{program.rewardPerMonthEur} voor elke maand dat je
        meedoet, tot €{program.maxRewardEur}.
      </p>
      <PeakWindowBar start={program.peakWindow.start} end={program.peakWindow.end} />
    </section>
  )
}

function Waitlist({ program, addressUuid }: { program: ProgramInfo; addressUuid: string | null }) {
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle')
  async function join() {
    setState('busy')
    try {
      await programsApi.joinWaitlist(program.slug, addressUuid ?? undefined)
      trackEvent('program_waitlist_join', { program: program.slug })
      setState('done')
    } catch {
      setState('error')
    }
  }
  return (
    <div className="flex flex-col gap-3 rounded-20 bg-beige p-5">
      <p className="text-base font-bold">Aanmelden is gesloten</p>
      <p className="text-15 leading-normal text-dark-blue/70">
        Er zit geen volle maand meer tussen nu en {formatProgramDate(program.endsAt)}. Binnenkort volgen
        nieuwe diensten waarmee Sparky je flexibiliteit beloont.
      </p>
      {state === 'done' ? (
        <p className="text-15 font-semibold">We houden je op de hoogte.</p>
      ) : (
        <button type="button" className="btn-secondary self-start" onClick={join} disabled={state === 'busy'}>
          Houd me op de hoogte
        </button>
      )}
      {state === 'error' && <p className="text-15 font-semibold text-red">Dat is niet gelukt. Probeer het later opnieuw.</p>}
    </div>
  )
}

function HowItWorks({
  program,
  onStart,
  addressUuid,
}: {
  program: ProgramInfo
  onStart: () => void
  addressUuid: string | null
}) {
  const { start, end } = program.peakWindow
  const steps = [
    {
      title: 'Meld je aan',
      body: 'Kies je adres, koppel je auto en Sparky of Flint (en je laadpaal, als je die hebt), en ga akkoord met de voorwaarden.',
    },
    {
      title: `Laad niet tussen ${start} en ${end}`,
      body: `Elke dag tot ${formatProgramDate(program.endsAt)}. We controleren je laadsessies en of alles gekoppeld blijft.`,
    },
    {
      title: 'Ontvang je beloning',
      body: `€${program.rewardPerMonthEur} per maand dat je meedoet, in één keer na afloop van de pilot.`,
    },
  ]
  return (
    <section className="card flex flex-col gap-6 p-6 md:p-10">
      <h2 className="text-26 font-bold lg:text-28">Zo werkt het</h2>
      <ol className="flex flex-col gap-5">
        {steps.map((s, i) => (
          <li key={s.title} className="flex items-start gap-4">
            <span
              className={`flex size-10 shrink-0 items-center justify-center rounded-full text-base font-bold text-white ${STEP_CIRCLE[i]}`}
            >
              {i + 1}
            </span>
            <div>
              <p className="text-lg font-bold">{s.title}</p>
              <p className="mt-0.5 text-base leading-normal text-dark-blue/70">{s.body}</p>
            </div>
          </li>
        ))}
      </ol>
      <div className="border-t border-beige-2 pt-6">
        {program.signup.state === 'open' && (
          <>
            <button
              type="button"
              className="btn-cta !h-14 w-full !rounded-full !text-lg !font-bold shadow-[0_8px_24px_rgba(98,69,222,0.35)]"
              onClick={onStart}
            >
              Doe mee en verdien tot €{program.signup.nextRewardEur} →
            </button>
            <p className="mt-3 text-center text-13 text-dark-blue/60">
              {startsRightAway(program.signup.nextStartDate)
                ? 'Je doet direct mee'
                : `Start op ${formatProgramDate(program.signup.nextStartDate)}`}{' '}
              · Aanmelden duurt 2 minuten
            </p>
          </>
        )}
        {program.signup.state === 'closed' && <Waitlist program={program} addressUuid={addressUuid} />}
        {program.signup.state === 'not_yet_open' && (
          <div className="rounded-20 bg-beige p-5 text-15 text-dark-blue/70">
            Aanmelden is nog niet geopend. Kom binnenkort terug.
          </div>
        )}
      </div>
    </section>
  )
}

/**
 * For someone already taking part: no sign-up pitch, just their participation
 * per address and, when another address could still join, the way to add it.
 */
function Participating({
  program,
  mine,
  addresses,
  canAddAnother,
  onAddAnother,
}: {
  program: ProgramInfo
  mine: Map<string, Participation>
  addresses: V5AddressSummary[] | null
  canAddAnother: boolean
  onAddAnother: () => void
}) {
  const labelOf = (uuid: string) => {
    const a = addresses?.find((x) => x.addressUuid === uuid)
    return a ? addressLabel(a) : 'Adres'
  }
  return (
    <section className="card flex flex-col gap-6 p-6 md:p-10">
      <div>
        <h2 className="text-26 font-bold lg:text-28">Je doet mee</h2>
        <p className="mt-1.5 text-base text-dark-blue/70">
          {mine.size === 1 ? 'Dit adres doet mee' : 'Deze adressen doen mee'} aan {program.name}.
        </p>
      </div>
      <ul className="flex flex-col gap-2.5">
        {[...mine.values()].map((p) => {
          const badge = participationBadge(p)
          return (
            <li
              key={p.id}
              className="flex flex-col gap-3 rounded-20 bg-beige px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <p className="text-base font-bold">{labelOf(p.addressUuid)}</p>
                <p className="text-13 text-dark-blue/60">Start {formatProgramDate(p.startDate)}</p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <StatusPill label={badge.label} tone={badge.tone} />
                <Link to="/programs" className="btn-cta !h-10 !px-4 !text-13">
                  Bekijk je deelname
                </Link>
              </div>
            </li>
          )
        })}
      </ul>
      {canAddAnother && (
        <div className="flex flex-col gap-3 border-t border-beige-2 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-15 text-dark-blue/70">Wil je nog een adres laten meedoen?</p>
          <button type="button" className="btn-secondary shrink-0" onClick={onAddAnother}>
            Meld een ander adres aan
          </button>
        </div>
      )}
    </section>
  )
}

function GoodToKnow({ program }: { program: ProgramInfo }) {
  const { start, end } = program.peakWindow
  const open = program.signup.state === 'open'
  const items = [
    {
      q: 'Heb je een laadpaal nodig',
      a: `Nee. Je hebt een elektrische auto en een Sparky of Flint nodig. Laad je met een stopcontact of een laadpaal die niet gekoppeld is, dan zien we dat via je slimme meter. Heb je wel een laadpaal, koppel die dan in de ${APP_NAME}: dan doet die ook mee.`,
    },
    {
      q: 'Wat controleren we',
      a: `We volgen je laadsessies en of je auto, Sparky of Flint en een eventuele laadpaal verbonden blijven. Laad je tussen ${start} en ${end} of verbreek je een koppeling, dan stop je zonder beloning.`,
    },
    {
      q: 'Wanneer start je',
      a: `Op de eerste van de volgende maand. Meld je je in de eerste vijf dagen van een maand aan, dan doe je die maand nog mee en start je direct.${
        !open
          ? ''
          : startsRightAway(program.signup.nextStartDate)
            ? ' Meld je je nu aan, dan start je direct.'
            : ` Meld je je nu aan, dan start je op ${formatProgramDate(program.signup.nextStartDate)}.`
      }`,
    },
    {
      q: 'Stoppen',
      a: 'Dat kan altijd via Mijn programma’s. Je stopt per de eerste van de volgende maand en ontvangt dan geen beloning.',
    },
    {
      q: 'Wat krijg je',
      a: `€${program.rewardPerMonthEur} voor elke maand dat je meedoet, tot €${program.maxRewardEur} voor de hele pilot.${open ? ` Meld je je nu aan, dan is dat €${program.signup.nextRewardEur}.` : ''} Je krijgt het in één keer na afloop, van je energieleverancier of Chargee. Dit is de eerste manier waarop Sparky je flexibiliteit beloont.`,
    },
    {
      q: 'Gegevens delen',
      a: 'Voor deze pilot delen we gegevens over je deelname en verbruik met Netbeheer Nederland. Daar vragen we apart toestemming voor.',
    },
  ]
  return (
    <section className="px-2">
      <h2 className="mb-2 text-lg font-bold">Goed om te weten</h2>
      {items.map((item) => (
        <details key={item.q} className="group border-t border-beige-2 py-4">
          <summary className="flex cursor-pointer list-none items-center justify-between text-base font-bold [&::-webkit-details-marker]:hidden">
            {item.q}
            <span className="text-dark-blue/40 transition-transform duration-200 group-open:rotate-45" aria-hidden="true">
              +
            </span>
          </summary>
          <p className="mt-2 text-15 leading-normal text-dark-blue/70">{item.a}</p>
        </details>
      ))}
    </section>
  )
}

// --- Wizard -------------------------------------------------------------------

function ProgressBar({ step }: { step: Step }) {
  return (
    <div className="flex gap-1.5" aria-hidden="true">
      {[1, 2, 3].map((i) => (
        <span key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-dark-purple' : 'bg-beige-2'}`} />
      ))}
    </div>
  )
}

function AddressStep({
  addresses,
  taken,
  selected,
  onSelect,
  eligibility,
  loading,
  error,
}: {
  addresses: V5AddressSummary[] | null
  /** Addresses that already have their turn in this program. */
  taken: Map<string, Participation>
  selected: string | null
  onSelect: (uuid: string) => void
  eligibility: Eligibility | null
  loading: boolean
  error: string
}) {
  if (addresses === null) {
    return (
      <div className="flex items-center gap-3 text-base text-dark-blue/60">
        <Spinner /> Adressen laden…
      </div>
    )
  }
  if (addresses.length === 0) {
    return (
      <p className="text-base text-dark-blue/70">
        Er is nog geen adres gekoppeld aan je account. Voeg je adres toe in de {APP_NAME} en kom daarna terug.
      </p>
    )
  }
  const block =
    eligibility?.blockReason === 'ADDRESS_NOT_ELIGIBLE'
      ? ADDRESS_ISSUE_MESSAGE[eligibility.addressIssue ?? 'country']
      : eligibility?.blockReason
        ? BLOCK_MESSAGES[eligibility.blockReason]
        : null
  const noneEligible = addresses.every((a) => addressIssue(a) !== null)
  return (
    <div className="flex flex-col gap-2.5" role="radiogroup" aria-label="Adres">
      {addresses.map((a) => {
        const checked = a.addressUuid === selected
        const already = taken.get(a.addressUuid)
        const issue = addressIssue(a)
        const unavailable = Boolean(already) || issue !== null
        const city = [a.postalCode, a.city].filter(Boolean).join(' ')
        const street = [a.street, a.houseNumber, a.houseNumberAdd].filter(Boolean).join(' ') || addressLabel(a)
        return (
          <button
            key={a.addressUuid}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-disabled={unavailable ? true : undefined}
            disabled={unavailable}
            onClick={() => onSelect(a.addressUuid)}
            className={`flex items-center gap-4 rounded-20 px-5 py-4 text-left transition-shadow duration-200 ${
              unavailable
                ? 'cursor-not-allowed bg-beige ring-1 ring-inset ring-beige-2'
                : checked
                  ? 'bg-white ring-2 ring-inset ring-dark-purple'
                  : 'bg-white ring-1 ring-inset ring-beige-2 hover:ring-dark-blue/30'
            }`}
          >
            <span
              className={`flex size-5 shrink-0 items-center justify-center rounded-full border-2 ${
                checked ? 'border-dark-purple' : 'border-beige-2'
              }`}
              aria-hidden="true"
            >
              {checked && <span className="size-2.5 rounded-full bg-dark-purple" />}
            </span>
            <span className="min-w-0 flex-1">
              <span className={`block text-base font-bold ${unavailable ? 'text-dark-blue/50' : ''}`}>{street}</span>
              {city && <span className="block text-13 text-dark-blue/60">{city}</span>}
            </span>
            {already && <StatusPill label="Doet al mee" tone="muted" />}
            {!already && issue && <StatusPill label={ADDRESS_ISSUE_LABEL[issue]} tone="muted" />}
          </button>
        )
      })}
      {noneEligible && (
        <p className="rounded-20 bg-beige p-4 text-15 text-dark-blue/80">
          Deze pilot is alleen voor adressen in Nederland met een geldige postcode. Geen van je adressen
          voldoet daaraan.
        </p>
      )}
      {selected && loading && (
        <p className="flex items-center gap-2 text-15 text-dark-blue/60">
          <Spinner className="size-4" /> Adres controleren…
        </p>
      )}
      {error && <p className="text-15 font-semibold text-red">{error}</p>}
      {block && <p className="rounded-20 bg-beige p-4 text-15 text-dark-blue/80">{block}</p>}
    </div>
  )
}

function ConfirmStep({
  eligibility,
  address,
  consents,
  setConsents,
}: {
  eligibility: Eligibility
  address: V5AddressSummary | null
  consents: Consents
  setConsents: (c: Consents) => void
}) {
  const { program } = eligibility
  const rows = [
    { label: 'Adres', value: address ? addressLabel(address) : '' },
    { label: 'Start', value: startsRightAway(eligibility.startDate) ? 'Direct' : formatProgramDate(eligibility.startDate) },
    {
      label: 'Niet laden',
      value: `${program.peakWindow.start} – ${program.peakWindow.end}, tot ${formatProgramDate(program.endsAt)}`,
    },
    { label: 'Beloning', value: `€${eligibility.rewardEur} als je meedoet tot het einde` },
  ]
  return (
    <div className="flex flex-col gap-5">
      <dl className="flex flex-col gap-2 rounded-20 bg-beige px-5 py-4">
        {rows.map((r) => (
          <div key={r.label} className="flex flex-wrap justify-between gap-x-4 text-15">
            <dt className="text-dark-blue/60">{r.label}</dt>
            <dd className="text-right font-semibold">{r.value}</dd>
          </div>
        ))}
      </dl>
      <div className="flex flex-col gap-3">
        <label className="flex items-start gap-3 text-base leading-normal">
          <input
            type="checkbox"
            className="mt-1 size-5 shrink-0 accent-dark-purple"
            checked={consents.program}
            onChange={(e) => setConsents({ ...consents, program: e.target.checked })}
          />
          <span>
            Ik ga akkoord met de{' '}
            <a href={program.terms.program.url} target="_blank" rel="noreferrer" className="font-semibold text-dark-purple underline">
              voorwaarden van {program.name}
            </a>
            .
          </span>
        </label>
        <label className="flex items-start gap-3 text-base leading-normal">
          <input
            type="checkbox"
            className="mt-1 size-5 shrink-0 accent-dark-purple"
            checked={consents.dataSharing}
            onChange={(e) => setConsents({ ...consents, dataSharing: e.target.checked })}
          />
          <span>
            Ik ga akkoord met het{' '}
            <a href={program.terms.dataSharing.url} target="_blank" rel="noreferrer" className="font-semibold text-dark-purple underline">
              delen van gegevens met Netbeheer Nederland
            </a>
            .
          </span>
        </label>
      </div>
    </div>
  )
}

function Confirmation({ participation, program }: { participation: Participation; program: ProgramInfo }) {
  const p1 = participation.devices.find((d) => d.role === 'p1')?.name ?? 'Sparky'
  const cars = participation.devices.filter((d) => d.role === 'ev').length
  const chargers = participation.devices.filter((d) => d.role === 'charger').length
  const kept = [
    cars === 1 ? 'auto' : 'auto’s',
    ...(chargers === 0 ? [] : [chargers === 1 ? 'laadpaal' : 'laadpalen']),
    p1,
  ]
    .join(', ')
    .replace(/, ([^,]*)$/, ' en $1')
  const now = startsRightAway(participation.startDate)
  const noCharging = `niet laden tussen ${program.peakWindow.start} en ${program.peakWindow.end}`
  const timeline = [
    now
      ? { when: 'Vandaag', what: `Aangemeld en gestart: ${noCharging}`, dot: 'bg-green' }
      : { when: 'Vandaag', what: 'Aangemeld', dot: 'bg-green' },
    ...(now
      ? []
      : [{ when: formatProgramDate(participation.startDate), what: `Start: ${noCharging}`, dot: 'bg-warning' }]),
    { when: formatProgramDate(program.endsAt), what: 'Einde van de pilot', dot: 'bg-beige-2' },
    { when: 'Daarna', what: `Uitbetaling van je beloning van €${participation.rewardEur}`, dot: 'bg-beige-2' },
  ]
  return (
    <section className="card mx-auto flex w-full max-w-[640px] flex-col gap-6 p-6 md:p-10">
      <span className="flex size-14 items-center justify-center rounded-full bg-light-green">
        <CheckIcon className="size-7" />
      </span>
      <div>
        <h1 className="text-28 font-bold lg:text-[32px]">Je doet mee</h1>
        <p className="mt-2 text-base leading-normal text-dark-blue/70">
          Vanaf <strong className="text-dark-blue">{now ? 'nu' : formatProgramDate(participation.startDate)}</strong> laad je niet
          thuis tussen {program.peakWindow.start} en {program.peakWindow.end}. Houd je {kept} gekoppeld tot{' '}
          {formatProgramDate(program.endsAt)}.
        </p>
      </div>
      <ol className="flex flex-col">
        {timeline.map((t, i) => (
          <li key={t.what} className="relative flex gap-4 pb-5 last:pb-0">
            {i < timeline.length - 1 && (
              <span className="absolute left-[5px] top-4 h-full w-0.5 bg-beige-2" aria-hidden="true" />
            )}
            <span className={`relative mt-1.5 size-3 shrink-0 rounded-full ${t.dot}`} aria-hidden="true" />
            <div>
              <p className="text-13 font-semibold text-dark-blue/60">{t.when}</p>
              <p className="text-base font-semibold">{t.what}</p>
            </div>
          </li>
        ))}
      </ol>
      <Link to="/programs" className="btn-cta !h-14 w-full !rounded-full !text-lg !font-bold">
        Bekijk je deelname
      </Link>
    </section>
  )
}

// --- Page ---------------------------------------------------------------------

export function ProgramLandingPage() {
  const { slug = '' } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const addressParam = searchParams.get('addressUuid')

  const [program, setProgram] = useState<ProgramInfo | null>(null)
  const [programError, setProgramError] = useState('')
  const [addresses, setAddresses] = useState<V5AddressSummary[] | null>(null)
  // The user's own sign-ups for this program, per address; null while loading.
  const [mine, setMine] = useState<Map<string, Participation> | null>(null)
  const [eligibility, setEligibility] = useState<Eligibility | null>(null)
  const [eligibilityError, setEligibilityError] = useState('')
  const [loadingEligibility, setLoadingEligibility] = useState(false)

  const [phase, setPhase] = useState<Phase>('intro')
  const [step, setStep] = useState<Step>(1)
  const [consents, setConsents] = useState<Consents>({ program: false, dataSharing: false })
  const [joining, setJoining] = useState(false)
  const [joinError, setJoinError] = useState('')
  const [joined, setJoined] = useState<Participation | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    programsApi
      .getProgram(slug, controller.signal)
      .then((p) => {
        setProgram(p)
        trackEvent('program_landing_view', { program: p.slug, signup: p.signup.state })
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        setProgramError(
          programsApi.programsErrorCode(err) === 'PROGRAM_NOT_FOUND'
            ? 'Dit programma bestaat niet (meer).'
            : 'We konden het programma niet laden. Probeer het later opnieuw.'
        )
      })
    v5.getAddresses()
      .then((list) => !controller.signal.aborted && setAddresses(list))
      .catch(() => !controller.signal.aborted && setAddresses([]))
    programsApi
      .listParticipations(controller.signal, { withDevices: false })
      .then((list) => !controller.signal.aborted && setMine(participationsByAddress(list, slug)))
      // Without the list the API still refuses a second sign-up; the page just cannot say so up front.
      .catch(() => !controller.signal.aborted && setMine(new Map()))
    return () => controller.abort()
  }, [slug])

  // Addresses that can still sign up: the user's own, minus those already taking part.
  const available = useMemo(
    () =>
      addresses && mine ? addresses.filter((a) => !mine.has(a.addressUuid) && addressIssue(a) === null) : null,
    [addresses, mine]
  )

  // The invitation's address if it can still sign up, else the only one that can.
  const selected = useMemo(() => {
    if (!available) return null
    if (addressParam && available.some((a) => a.addressUuid === addressParam)) return addressParam
    return available.length === 1 ? available[0]!.addressUuid : null
  }, [available, addressParam])
  const selectedAddress = addresses?.find((a) => a.addressUuid === selected) ?? null

  const selectAddress = useCallback(
    (uuid: string) => setSearchParams({ addressUuid: uuid }, { replace: true }),
    [setSearchParams]
  )

  const loadEligibility = useCallback(
    (signal?: AbortSignal) => {
      if (!selected) return
      setLoadingEligibility(true)
      setEligibilityError('')
      programsApi
        .getEligibility(slug, selected, signal)
        .then((result) => {
          setEligibility(result)
          trackEvent('program_eligibility_result', {
            program: slug,
            eligible: result.devicesEligible,
            missing_count: result.checks.filter((c) => c.required && !c.ok).length,
            block: result.blockReason ?? 'none',
          })
        })
        .catch((err) => {
          if (signal?.aborted) return
          setEligibility(null)
          setEligibilityError(
            programsApi.programsErrorCode(err) === 'ADDRESS_NOT_FOUND'
              ? 'We konden dit adres niet vinden bij je account.'
              : 'We konden je gegevens niet ophalen. Probeer het later opnieuw.'
          )
        })
        .finally(() => !signal?.aborted && setLoadingEligibility(false))
    },
    [slug, selected]
  )

  useEffect(() => {
    setEligibility(null)
    if (!selected) return
    const controller = new AbortController()
    loadEligibility(controller.signal)
    return () => controller.abort()
  }, [selected, loadEligibility])

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [phase, step])

  function startWizard() {
    setPhase('wizard')
    setStep(1)
    trackEvent('program_wizard_step', { program: slug, step: 1 })
  }

  function back() {
    if (step === 1) setPhase('intro')
    else setStep((s) => (s - 1) as Step)
  }

  async function next() {
    if (step < 3) {
      const to = (step + 1) as Step
      setStep(to)
      trackEvent('program_wizard_step', { program: slug, step: to })
      return
    }
    if (!eligibility || joining) return
    setJoining(true)
    setJoinError('')
    trackEvent('program_join_submit', { program: slug })
    try {
      const participation = await programsApi.enrol(slug, {
        addressUuid: eligibility.addressUuid,
        tcProgramVersion: eligibility.program.terms.program.version,
        tcDataSharingVersion: eligibility.program.terms.dataSharing.version,
      })
      trackEvent('program_join_success', { program: slug })
      setJoined(participation)
      setMine((m) => new Map(m ?? []).set(participation.addressUuid, participation))
      setPhase('done')
    } catch (err) {
      const code = programsApi.programsErrorCode(err)
      trackEvent('program_join_error', { program: slug, code: code ?? 'network' })
      setJoinError(
        code === 'DEVICE_IN_USE_ELSEWHERE'
          ? deviceInUseMessage(programsApi.programsErrorDetails(err))
          : ((code && JOIN_ERRORS[code]) ?? 'Aanmelden is niet gelukt. Probeer het later opnieuw.')
      )
      if (code === 'NOT_ELIGIBLE') {
        setStep(2)
        loadEligibility()
      } else if (code === 'TERMS_VERSION_MISMATCH') {
        setConsents({ program: false, dataSharing: false })
        loadEligibility()
      }
    } finally {
      setJoining(false)
    }
  }

  const blocker =
    step === 1 && !selected
      ? 'Kies een adres om verder te gaan.'
      : step === 1 && (!eligibility || loadingEligibility)
        ? null
        : wizardBlocker(step, eligibility, consents)
  const canContinue =
    !joining &&
    !!eligibility &&
    !loadingEligibility &&
    (step === 1 ? eligibility.blockReason === null : blocker === null)
  const items = eligibility ? buildChecklist(eligibility, consents).slice(0, 3) : []

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-10 pt-12">
      <nav className="mb-6 text-15 text-dark-blue/60">
        <Link to="/services" className="transition-all duration-300 hover:text-dark-blue">
          Diensten
        </Link>{' '}
        <span className="mx-1">/</span>
        <span className="text-dark-blue">{program?.name ?? 'Programma'}</span>
      </nav>

      {programError && (
        <div className="card-beige p-5 text-base font-semibold text-red md:p-[30px]">{programError}</div>
      )}

      {!programError && !program && (
        <div className="flex items-center gap-3 py-10 text-base text-dark-blue/60">
          <Spinner /> Programma laden…
        </div>
      )}

      {program && phase === 'intro' && (
        <div className="mx-auto flex max-w-[860px] flex-col gap-5">
          <Hero program={program} />
          {mine === null && (
            <div className="flex items-center gap-3 px-2 text-base text-dark-blue/60">
              <Spinner /> Je deelname controleren…
            </div>
          )}
          {mine !== null && mine.size > 0 && (
            <Participating
              program={program}
              mine={mine}
              addresses={addresses}
              canAddAnother={program.signup.state === 'open' && (available?.length ?? 0) > 0}
              onAddAnother={startWizard}
            />
          )}
          {mine !== null && mine.size === 0 && (
            <HowItWorks program={program} onStart={startWizard} addressUuid={selected} />
          )}
          <GoodToKnow program={program} />
        </div>
      )}

      {program && phase === 'wizard' && (
        <section className="card mx-auto flex w-full max-w-[640px] flex-col gap-6 p-6 md:p-10">
          <div className="flex items-center justify-between">
            <button type="button" className="btn-text" onClick={back}>
              ← Terug
            </button>
            <span className="text-13 font-bold text-dark-blue/50">Stap {step} van 3</span>
          </div>
          <ProgressBar step={step} />
          <div>
            <h1 className="text-26 font-bold leading-111 lg:text-[30px]">{STEP_COPY[step].title}</h1>
            <p className="mt-1.5 text-base text-dark-blue/70">{STEP_COPY[step].subtitle}</p>
          </div>

          {step === 1 && (
            <AddressStep
              addresses={addresses}
              taken={mine ?? new Map()}
              selected={selected}
              onSelect={selectAddress}
              eligibility={eligibility}
              loading={loadingEligibility}
              error={eligibilityError}
            />
          )}
          {step === 2 && eligibility && (
            <EligibilityChecklist items={items} onRecheck={() => loadEligibility()} rechecking={loadingEligibility} />
          )}
          {step === 3 && eligibility && (
            <ConfirmStep
              eligibility={eligibility}
              address={selectedAddress}
              consents={consents}
              setConsents={setConsents}
            />
          )}

          <div className="border-t border-beige-2 pt-6">
            {joinError && <p className="mb-3 text-15 font-semibold text-red">{joinError}</p>}
            <button
              type="button"
              className="btn-cta !h-14 w-full !rounded-full !text-lg !font-bold"
              disabled={!canContinue}
              onClick={next}
            >
              {step < 3 ? 'Volgende' : joining ? 'Aanmelden…' : 'Doe mee'}
            </button>
            {blocker && <p className="mt-3 text-center text-13 text-dark-blue/60">{blocker}</p>}
          </div>
        </section>
      )}

      {program && phase === 'done' && joined && <Confirmation participation={joined} program={program} />}
    </div>
  )
}
